// One Step 2 row: multi-select dropdown with catalog tools, Other (type it in) and an exclusive
// Not applicable; chips; Other text; tips; optional usage note.
import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, CircleCheck, ChevronDown, ChevronUp } from 'lucide-react';
import type { FunctionDef, ToolId, ToolRow } from '@upm/domain';
import { TokenChip, cx } from '../ui';
import { rowChips, toggleNotApplicable, toggleOther, toggleTool } from './logic';

type Option = { id: string; label: string };

export function ToolRowField({ fn, options, row, tips, onChange, flagUnanswered, otherError, disabled }: {
  fn: FunctionDef;
  options: Option[];
  row: ToolRow;
  tips: string[];
  onChange: (row: ToolRow, opts?: { typing?: boolean }) => void;
  flagUnanswered?: boolean;
  otherError?: string | null;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(row.usageNote !== null && row.usageNote !== '');
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const uid = useId();
  const listId = `${uid}-list`;
  const labels = Object.fromEntries(options.map((o) => [o.id, o.label]));
  const chips = rowChips(row, labels);
  const unanswered = !row.notApplicable && row.tools.length === 0 && row.other === null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) wrap.current?.querySelector<HTMLInputElement>('input[type=checkbox]')?.focus();
  }, [open]);

  const close = () => { setOpen(false); trigger.current?.focus(); };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const boxes = [...(wrap.current?.querySelectorAll<HTMLInputElement>('input[type=checkbox]') ?? [])];
    const i = boxes.indexOf(document.activeElement as HTMLInputElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); boxes[Math.min(boxes.length - 1, i + 1)]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); boxes[Math.max(0, i - 1)]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); boxes[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); boxes[boxes.length - 1]?.focus(); }
  };

  const summary = chips.length ? chips.map((c) => c.label).join(', ') : 'nothing selected';
  const flagged = !!flagUnanswered && unanswered;
  const triggerText = chips.length ? 'Edit tools' : 'Select tools';
  const removeChip = (key: string) => {
    if (key === 'na') onChange(toggleNotApplicable(row));
    else if (key === 'other') onChange(toggleOther(row));
    else onChange(toggleTool(row, key as ToolId));
    trigger.current?.focus();
  };
  const all: Array<{ key: string; label: string; checked: boolean; toggle: () => void; divider?: boolean }> = [
    ...options.map((o) => ({ key: o.id, label: o.label, checked: row.tools.includes(o.id), toggle: () => onChange(toggleTool(row, o.id)) })),
    { key: '__other', label: 'Other (type it in)', checked: row.other !== null, toggle: () => onChange(toggleOther(row)), divider: true },
    { key: '__na', label: 'Not applicable, we don’t do this', checked: row.notApplicable, toggle: () => onChange(toggleNotApplicable(row)) },
  ];

  return (
    <div
      data-testid={`tool-row-${fn.id}`}
      data-unanswered={unanswered || undefined}
      className={cx('flex flex-col gap-3 rounded-card border px-4 py-4 sm:px-5 sm:py-[18px]', flagged ? 'border-warn-line bg-warn-tint' : 'border-line bg-white')}
    >
      {/* Below sm the list spans this whole header row (the button may sit left or right); from sm it hangs off the button's right edge. */}
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-[3px]">
          <div id={`${uid}-label`} className="text-base font-semibold">{fn.label}</div>
          {flagged
            ? <div className="text-sm text-warn-fg"><span>No answer yet</span>. Needed to submit: pick your tools, Other or Not applicable.</div>
            : <div className="text-sm text-muted">{fn.hint}</div>}
        </div>
        <div ref={wrap} className="flex-none sm:relative">
          <button
            ref={trigger}
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-label={`${fn.label}: ${summary} (${triggerText})`}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
            className={cx(
              'flex min-h-11 items-center gap-2 rounded-[10px] border bg-white px-3.5 text-sm text-ink disabled:text-muted',
              flagged || open ? 'border-brand font-semibold text-brand' : 'border-line-btn font-medium hover:bg-panel',
            )}
          >
            {triggerText}
            {open ? <ChevronUp aria-hidden="true" size={16} strokeWidth={2} /> : <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />}
          </button>
          {open && (
            <div className="absolute inset-x-0 top-full z-30 mt-1.5 rounded-xl sm:left-auto sm:right-0 sm:w-[340px] border border-line bg-white p-1.5 shadow-pop" onKeyDown={onListKey}>
              <div id={listId} role="group" aria-labelledby={`${uid}-label`} className="flex max-h-[300px] flex-col gap-0.5 overflow-auto">
                {all.map((o) => (
                  <Fragment key={o.key}>
                  {o.divider && <div aria-hidden="true" className="mx-2.5 my-1 border-t border-line-soft" />}
                  <label
                    className={cx(
                      // Keyboard focus draws inside the option, so neighbours and the scrolling list can't clip it; a mouse click draws none.
                      'relative flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-sm text-ink [&:has(input:focus-visible)]:shadow-[inset_0_0_0_2px_rgb(var(--brand-ring))]',
                      o.checked ? 'bg-brand-tint font-semibold' : 'bg-white font-normal hover:bg-panel',
                    )}
                  >
                    <input type="checkbox" className="absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0" checked={o.checked} onChange={o.toggle} />
                    <span aria-hidden="true" className={cx('box-border flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] border-[1.5px]', o.checked ? 'border-brand bg-brand' : 'border-line-strong bg-white')}>
                      {o.checked && <Check size={12} strokeWidth={3.5} className="text-white" />}
                    </span>
                    <span>{o.label}</span>
                  </label>
                  </Fragment>
                ))}
              </div>
              <div className="mt-1 flex justify-end border-t border-line-soft px-1 pb-0.5 pt-2">
                <button type="button" onClick={close} className="min-h-9 rounded-lg border-0 bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <TokenChip key={c.key} muted={c.na} onRemove={disabled ? undefined : () => removeChip(c.key)} removeLabel={`Remove ${c.label}`}>{c.label}</TokenChip>
          ))}
        </div>
      )}
      {row.other !== null && (
          <div className="max-w-[420px]">
            <label htmlFor={`${uid}-other`} className="mb-1.5 block text-[13px] font-semibold text-ink-2">Which other tool?</label>
            <input
              id={`${uid}-other`}
              type="text"
              required
              disabled={disabled}
              value={row.other}
              placeholder="Type the tool's name"
              aria-invalid={otherError ? true : undefined}
              aria-describedby={otherError ? `${uid}-other-err` : undefined}
              onChange={(e) => onChange({ ...row, other: e.target.value }, { typing: true })}
              className={cx('input', otherError && 'input-invalid')}
            />
            {otherError && <div id={`${uid}-other-err`} className="mt-1 text-[13px] font-semibold text-bad">{otherError}</div>}
          </div>
        )}
        {tips.map((tip) => (
          <div key={tip} className="flex items-start gap-2.5 rounded-[10px] bg-ok-bg px-3 py-2.5 text-sm text-ok-fg">
            <CircleCheck aria-hidden="true" size={18} strokeWidth={2} className="mt-px flex-none" />
            <span>{tip}</span>
          </div>
        ))}
        {noteOpen && (
          <div>
            <label htmlFor={`${uid}-note`} className="mb-1.5 block text-[13px] font-semibold text-ink-2">How do you use it? <span className="font-normal">(optional)</span></label>
            <textarea
              id={`${uid}-note`}
              rows={2}
              disabled={disabled}
              value={row.usageNote ?? ''}
              placeholder="For example: jobs and due dates live here, but extensions are tracked in a spreadsheet"
              onChange={(e) => onChange({ ...row, usageNote: e.target.value }, { typing: true })}
              className="box-border w-full resize-y rounded-[10px] border border-line-strong bg-white px-3 py-2.5 text-sm"
            />
          </div>
        )}
        {!noteOpen && !unanswered && !row.notApplicable && !disabled && (
          <button type="button" onClick={() => setNoteOpen(true)} className="min-h-8 self-start rounded border-0 bg-transparent p-0 text-[13px] font-semibold text-brand hover:underline">
            + Add how you use it
          </button>
        )}
    </div>
  );
}
